import { LATEST_EDIT_WINS, q, syncTable, upsertFromJsonSql } from '@quickreport/db/sync-tables';
import { newId } from '@quickreport/domain';
import { kvGet, kvSet, onLocalChange } from './queue';
import {
  AuthError,
  type LocalDb,
  OfflineError,
  type PushChange,
  type PushResult,
  type Remote,
  type Row,
  type SyncStatus,
} from './types';

const PUSH_BATCH = 100;
const PULL_PAGE = 500;
const SYNC_INTERVAL_MS = 30_000;

export interface EngineOptions {
  db: LocalDb;
  remote: Remote;
  orgId: string;
  /** Called with every status change. */
  onStatus?: (s: SyncStatus) => void;
  /** Turn off timers and listeners (tests drive `syncNow` directly). */
  manual?: boolean;
}

/**
 * Keeps the local database and the server in step. Local edits are queued in the outbox;
 * `syncNow` pushes them, applies the server's changes, and moves image files. It never loses
 * a local edit: an edit leaves the outbox only after the server accepted it or told the
 * person it was overwritten (kept in sync_conflicts).
 */
export class SyncEngine {
  private status: SyncStatus = {
    state: 'idle',
    pending: 0,
    conflicts: 0,
    lastSyncedAt: null,
  };
  private running: Promise<void> | null = null;
  private again = false;
  private stopped = false;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private failures = 0;
  /** Background syncs wait until this time. Pushed forward by every keystroke (see `hold`). */
  private holdUntil = 0;
  private holdTimer: ReturnType<typeof setTimeout> | null = null;
  private cleanups: (() => void)[] = [];
  private readonly listeners = new Set<(s: SyncStatus) => void>();

  constructor(private readonly o: EngineOptions) {
    if (o.onStatus) this.listeners.add(o.onStatus);
  }

  getStatus(): SyncStatus {
    return this.status;
  }

  subscribe(fn: (s: SyncStatus) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** Begin syncing in the background. */
  start(): void {
    if (this.o.manual) return;
    this.stopped = false;
    let debounce: ReturnType<typeof setTimeout> | undefined;
    this.cleanups.push(
      onLocalChange(() => {
        clearTimeout(debounce);
        debounce = setTimeout(() => void this.syncSoon(), 3000);
      }),
    );
    // Sync again when the network returns or the window regains focus (browser and desktop).
    const target = globalThis as unknown as {
      addEventListener?: (t: string, fn: () => void) => void;
      removeEventListener?: (t: string, fn: () => void) => void;
    };
    if (target.addEventListener && target.removeEventListener) {
      const wake = () => void this.syncSoon();
      target.addEventListener('online', wake);
      target.addEventListener('focus', wake);
      this.cleanups.push(() => {
        target.removeEventListener?.('online', wake);
        target.removeEventListener?.('focus', wake);
      });
    }
    void this.refreshCounts();
    void this.syncNow();
  }

  stop(): void {
    this.stopped = true;
    if (this.timer) clearTimeout(this.timer);
    if (this.holdTimer) clearTimeout(this.holdTimer);
    for (const c of this.cleanups.splice(0)) c();
  }

  /**
   * Ask for background syncs to wait: nothing is sent or fetched until `ms` have passed with no
   * further call. The report editor calls this on every keystroke, so a sync never lands in the
   * middle of typing. Explicit `syncNow()` calls (signing, sending) are not held.
   */
  hold(ms: number): void {
    this.holdUntil = Math.max(this.holdUntil, Date.now() + ms);
  }

  /** A background sync: it waits for typing to stop, then runs. */
  syncSoon(): Promise<void> {
    const wait = this.holdUntil - Date.now();
    if (wait > 0 && !this.stopped) {
      if (this.holdTimer) clearTimeout(this.holdTimer);
      this.holdTimer = setTimeout(() => void this.syncSoon(), wait + 50);
      return Promise.resolve();
    }
    return this.syncNow();
  }

  /** Run a sync now, or as soon as the one in progress ends. */
  syncNow(): Promise<void> {
    if (this.running) {
      this.again = true;
      return this.running;
    }
    this.running = this.run().finally(() => {
      this.running = null;
      if (this.again && !this.stopped) {
        this.again = false;
        void this.syncNow();
      }
    });
    return this.running;
  }

  private set(patch: Partial<SyncStatus>) {
    // Listeners re-draw the screen, so say nothing when nothing changed.
    const next = { ...this.status, ...patch };
    const same = (Object.keys(next) as (keyof SyncStatus)[]).every(
      (k) => next[k] === this.status[k],
    );
    if (same) return;
    this.status = next;
    for (const fn of this.listeners) fn(this.status);
  }

  async refreshCounts(): Promise<void> {
    const pending = await this.o.db.query<{ n: number }>(
      'select count(*)::int n from (select 1 from outbox group by table_name, row_id) x',
    );
    const conflicts = await this.o.db.query<{ n: number }>(
      'select count(*)::int n from sync_conflicts where dismissed_at is null',
    );
    this.set({ pending: pending.rows[0]?.n ?? 0, conflicts: conflicts.rows[0]?.n ?? 0 });
  }

  private schedule(ms: number) {
    if (this.o.manual || this.stopped) return;
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.syncSoon(), ms);
  }

  private async run(): Promise<void> {
    if (this.status.state === 'signed_out') return;
    this.set({ state: 'syncing' });
    try {
      // Push first so a fast pull cannot overwrite an edit that is about to be sent.
      while (await this.pushBatch()) {
        /* keep going until the outbox is empty or blocked */
      }
      await this.pullAll();
      await this.syncFiles();
      this.failures = 0;
      await this.refreshCounts();
      this.set({ state: 'idle', lastSyncedAt: new Date().toISOString(), error: undefined });
      this.schedule(SYNC_INTERVAL_MS);
    } catch (err) {
      await this.refreshCounts().catch(() => undefined);
      this.failures++;
      const backoff = Math.min(30_000, 5_000 * 2 ** Math.min(this.failures, 3));
      if (err instanceof OfflineError) {
        this.set({ state: 'offline' });
        this.schedule(backoff);
      } else if (err instanceof AuthError) {
        this.set({ state: 'signed_out', authReason: err.reason });
      } else {
        this.set({ state: 'error', error: err instanceof Error ? err.message : String(err) });
        this.schedule(backoff);
      }
    }
  }

  // ---- push ------------------------------------------------------------------------

  /** Send one batch. Returns true when there may be more to send. */
  private async pushBatch(): Promise<boolean> {
    const { db, remote } = this.o;
    const entries = await db.query<{ table_name: string; row_id: string; seq: number }>(
      `select o.table_name, o.row_id, max(o.seq)::float as seq from outbox o
        group by o.table_name, o.row_id
        order by (o.table_name = 'reports'
                  and not exists (select 1 from reports r where r.id = o.row_id and r.deleted_at is not null)),
                 min(o.seq) limit ${PUSH_BATCH}`,
    );
    if (entries.rows.length === 0) return false;

    const changes: PushChange[] = [];
    const meta = new Map<string, { table: string; id: string; seq: number; localRow: Row }>();
    for (const e of entries.rows) {
      const t = syncTable(e.table_name);
      const seq = Number(e.seq);
      if (!t) {
        await db.query('delete from outbox where table_name = $1 and row_id = $2 and seq <= $3', [
          e.table_name,
          e.row_id,
          seq,
        ]);
        continue;
      }
      const r = await db.query<{ row: Row }>(
        `select to_jsonb(x) as row from ${q(t.name)} x where id = $1`,
        [e.row_id],
      );
      const row = r.rows[0]?.row;
      if (!row) {
        await db.query('delete from outbox where table_name = $1 and row_id = $2 and seq <= $3', [
          e.table_name,
          e.row_id,
          seq,
        ]);
        continue;
      }
      const changeId = `${e.table_name}:${e.row_id}:${seq}`;
      const deleted = t.hasDeletedAt && row.deleted_at != null && !t.insertOnly;
      changes.push({
        changeId,
        table: e.table_name,
        op: deleted ? 'delete' : 'upsert',
        id: e.row_id,
        baseRev: Number(row.rev ?? 0),
        ...(deleted ? {} : { row }),
      });
      meta.set(changeId, { table: e.table_name, id: e.row_id, seq, localRow: row });
    }
    if (changes.length === 0) return true;

    const { results } = await remote.push(changes);
    for (const res of results) {
      const m = meta.get(res.changeId);
      if (m) await this.settle(res, m);
    }
    await this.refreshCounts();
    return true;
  }

  private async settle(
    res: PushResult,
    m: { table: string; id: string; seq: number; localRow: Row },
  ) {
    const { db, remote } = this.o;
    const t = syncTable(m.table);
    if (!t) return;
    const clearQueue = (tx: { query: LocalDb['query'] }) =>
      tx.query('delete from outbox where table_name = $1 and row_id = $2 and seq <= $3', [
        m.table,
        m.id,
        m.seq,
      ]);

    if (res.status === 'applied') {
      await db.transaction(async (tx) => {
        if (res.rev > 0)
          await tx.query(`update ${q(t.name)} set rev = $2 where id = $1 and rev < $2`, [
            m.id,
            res.rev,
          ]);
        await clearQueue(tx);
      });
      return;
    }

    if (res.status === 'conflict' && LATEST_EDIT_WINS.has(t.name)) {
      // A newer edit from another device won; this one takes it, with nothing to settle.
      await db.transaction(async (tx) => {
        await tx.query(upsertFromJsonSql(t), [JSON.stringify(res.serverRow)]);
        await clearQueue(tx);
      });
      return;
    }

    let serverRow: Row | null;
    let reason: string;
    if (res.status === 'conflict') {
      serverRow = res.serverRow;
      reason = 'newer_on_server';
    } else {
      serverRow = await remote.getRow(m.table, m.id).catch(() => null);
      reason = `${res.code}: ${res.message}`;
    }
    await db.transaction(async (tx) => {
      await tx.query(
        `insert into sync_conflicts (id, table_name, row_id, local_row, server_row, reason)
         values ($1, $2, $3, $4::jsonb, $5::jsonb, $6)`,
        [
          newId(),
          m.table,
          m.id,
          JSON.stringify(m.localRow),
          JSON.stringify(serverRow ?? {}),
          reason,
        ],
      );
      // Put the server's version back, so this device agrees with everyone else.
      if (serverRow) await tx.query(upsertFromJsonSql(t), [JSON.stringify(serverRow)]);
      else await tx.query(`delete from ${q(t.name)} where id = $1`, [m.id]);
      await clearQueue(tx);
    });
  }

  // ---- pull ------------------------------------------------------------------------

  private async pullAll(): Promise<void> {
    const { db, remote, orgId } = this.o;
    const key = `pull_rev`;
    let cursor = Number((await kvGet(db, key, orgId)) ?? 0);
    for (let guard = 0; guard < 10_000; guard++) {
      const page = await remote.pull(cursor, PULL_PAGE);
      await db.transaction(async (tx) => {
        const pending = new Set(
          (
            await tx.query<{ k: string }>(
              `select distinct table_name || ':' || row_id as k from outbox`,
            )
          ).rows.map((r) => r.k),
        );
        for (const { table, row } of page.rows) {
          const t = syncTable(table);
          if (!t) continue;
          // A row with unsent local edits keeps them; the push settles who wins.
          if (pending.has(`${table}:${String(row.id)}`)) continue;
          await tx.query(upsertFromJsonSql(t, { onlyNewer: true }), [JSON.stringify(row)]);
        }
        await kvSet(tx, key, String(page.cursor), orgId);
      });
      cursor = page.cursor;
      if (!page.hasMore) return;
    }
  }

  // ---- files -----------------------------------------------------------------------

  private async syncFiles(): Promise<void> {
    const { db, remote } = this.o;
    const up = await db.query<{ id: string; mime: string; bytes: Uint8Array }>(
      `select f.id, f.mime, b.bytes from file_blobs b join files f on f.id = b.id
        where b.uploaded_at is null and f.rev > 0 limit 10`,
    );
    for (const f of up.rows) {
      try {
        await remote.putFile(f.id, new Uint8Array(f.bytes), f.mime);
        await db.query('update file_blobs set uploaded_at = now() where id = $1', [f.id]);
      } catch (err) {
        if (err instanceof OfflineError || err instanceof AuthError) throw err;
        // A file the server rejects stays local; it will be retried after the next change.
      }
    }
    const down = await db.query<{ id: string }>(
      `select f.id from files f left join file_blobs b on b.id = f.id
        where b.id is null and f.deleted_at is null and f.rev > 0 limit 20`,
    );
    for (const f of down.rows) {
      const bytes = await remote.getFile(f.id);
      if (bytes) {
        await db.query(
          `insert into file_blobs (id, bytes, uploaded_at) values ($1, $2, now()) on conflict (id) do nothing`,
          [f.id, bytes],
        );
      }
    }
  }
}
